import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const CATEGORIES = ["Grant", "Certification", "MoU", "Legal Contract", "License"];

export default function CompanyRecordFormDialog({ open, onOpenChange, onSaved }) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Certification");
  const [description, setDescription] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(""); setCategory("Certification"); setDescription(""); setIssueDate(""); setExpiryDate(""); setFile(null);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!title.trim() || !file) {
      toast.error("Title and a file are required");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("category", category);
      fd.append("description", description.trim());
      fd.append("issue_date", issueDate);
      fd.append("expiry_date", expiryDate);
      fd.append("file", file);
      await api.post("/company-records", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Record added");
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="company-record-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Company Record</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Title</Label>
            <Input data-testid="record-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. ISO 14001 Certification" />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="record-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Issue Date</Label>
              <Input data-testid="record-issue-date-input" type="date" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label>Expiry Date</Label>
              <Input data-testid="record-expiry-date-input" type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Description</Label>
            <Textarea data-testid="record-description-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1.5">
            <Label>Document File</Label>
            <Input data-testid="record-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="record-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="record-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : "Add Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
